import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";

export const CATEGORIES = [
  "Textbooks & Notes",
  "Tech & Electronics",
  "Dorm & Room",
  "Cycles & Mobility",
  "Fashion & Accessories",
];
const CONDITIONS = ["Like New", "Good", "Fair"];
const STOCK_IMG = {
  "Textbooks & Notes": "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Tech & Electronics": "https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Dorm & Room": "https://images.unsplash.com/photo-1555930112-0159bcdc3fe5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Cycles & Mobility": "https://images.unsplash.com/photo-1759823656479-461173524bf7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Fashion & Accessories": "https://images.unsplash.com/photo-1445205170230-053b83016050?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export default function CreateListingDialog({ open, onOpenChange, onCreated }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ title: "", description: "", price: "", category: "", condition: "", pickup_point_id: "", image: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  useEffect(() => {
    if (open) api.get("/addresses").then(({ data }) => setAddresses(data));
  }, [open]);

  const submit = async () => {
    if (!f.title || !f.price || !f.category || !f.condition || !f.pickup_point_id) {
      return toast.error("Please fill in all required fields");
    }
    setLoading(true);
    try {
      const { data } = await api.post("/listings", {
        title: f.title,
        description: f.description || "No description provided.",
        price: Number(f.price),
        category: f.category,
        condition: f.condition,
        images: [f.image || STOCK_IMG[f.category]],
        pickup_point_id: f.pickup_point_id,
      });
      toast.success("Listing posted to the marketplace!");
      onOpenChange(false);
      setF({ title: "", description: "", price: "", category: "", condition: "", pickup_point_id: "", image: "" });
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, "Could not post listing"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-listing-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Sell an item</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Casio FX-991EX Calculator" className="mt-1.5" data-testid="listing-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Price (₹) *</Label>
              <Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} placeholder="700" className="mt-1.5" data-testid="listing-price-input" />
            </div>
            <div>
              <Label>Condition *</Label>
              <Select value={f.condition} onValueChange={(v) => set("condition", v)}>
                <SelectTrigger className="mt-1.5" data-testid="listing-condition-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={f.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-1.5" data-testid="listing-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Campus pickup point *</Label>
            <Select value={f.pickup_point_id} onValueChange={(v) => set("pickup_point_id", v)}>
              <SelectTrigger className="mt-1.5" data-testid="listing-pickup-select"><SelectValue placeholder="Where will you hand it off?" /></SelectTrigger>
              <SelectContent>{addresses.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Item photo <span className="text-slate-400 font-normal">(optional)</span></Label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="listing-file-input" />
            {f.image ? (
              <div className="mt-1.5 relative rounded-lg overflow-hidden border border-slate-200">
                <img src={f.image} alt="preview" className="w-full h-40 object-cover" />
                <button type="button" onClick={() => set("image", "")} className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full bg-white/90 border border-slate-200 hover:bg-white" data-testid="listing-image-remove">
                  <X size={15} />
                </button>
              </div>
            ) : (
              <button
                type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="mt-1.5 w-full h-28 rounded-lg border-2 border-dashed border-slate-200 hover:border-brand text-slate-500 hover:text-brand flex flex-col items-center justify-center gap-1.5 transition-colors"
                data-testid="listing-upload-btn"
              >
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                <span className="text-sm">{uploading ? "Uploading…" : "Upload a photo from your device"}</span>
                <span className="text-xs text-slate-400">JPG, PNG or WebP · up to 6MB</span>
              </button>
            )}
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe condition, age, what's included…" className="mt-1.5" data-testid="listing-description-input" />
          </div>
          <Button onClick={submit} disabled={loading} className="w-full bg-ink hover:bg-slate-800 text-white h-11" data-testid="listing-submit-btn">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Post listing"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
